import { ArrowUpOutlined } from '@ant-design/icons'
import { Spin } from 'antd'
import React, { useState } from 'react'
import { useQuery } from 'react-query'
import styled from 'styled-components'
import { Popover } from 'sutra/components/Popover/Popover'

import { PostOverviewSection } from 'components/post-overview/PostOverviewSection'
import { buildFeedbackUrl } from 'features/ask-yogi/features/conversation/utils/url-builder'
import { useAskYogiService } from 'features/ask-yogi/service'
import type { AgentCitationPost } from 'features/ask-yogi/types'
import {
  Star,
  StarRow,
  VerbatimItem,
  VerbatimMeta,
  VerbatimMetaDot,
  VerbatimProductLine,
  VerbatimQuote,
  VerbatimSource,
} from 'features/trend-detection/components/insight-detail-styles'
import type { InsightVerbatim } from 'features/trend-detection/types'
import type { PostDetails } from 'types/project'

interface VerbatimCardProps {
  verbatim: InsightVerbatim
  projectId: string | null
}

export const VerbatimCard: React.FC<VerbatimCardProps> = ({
  verbatim,
  projectId,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const askYogiService = useAskYogiService()

  const { data: postData } = useQuery<AgentCitationPost | null>(
    ['citation', projectId, String(verbatim.record_id)],
    () =>
      askYogiService
        .getCitationPosts(projectId ?? '', [Number(verbatim.record_id)])
        .then((posts) => posts[0] ?? null),
    {
      enabled: isOpen && !!verbatim.record_id && !!projectId,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  )

  const verbatimUrl =
    postData?.post_uuid && projectId
      ? buildFeedbackUrl(projectId, null, postData.post_uuid)
      : null

  const popoverContent = postData ? (
    <PopoverBody>
      {verbatim.product && (
        <PopoverHeader>
          Record for: <em>{verbatim.product}</em>
        </PopoverHeader>
      )}
      <PostOverviewSection post={postData as unknown as PostDetails} stacked />
      {verbatimUrl && (
        <ViewRecordRow>
          <ViewRecordLink
            href={verbatimUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View record{' '}
            <ArrowUpOutlined
              style={{ marginLeft: '4px', transform: 'rotate(45deg)' }}
            />
          </ViewRecordLink>
        </ViewRecordRow>
      )}
    </PopoverBody>
  ) : (
    <PopoverLoadingWrap>
      <Spin size="small" />
    </PopoverLoadingWrap>
  )

  const card = (
    <VerbatimItem>
      <VerbatimQuote>&ldquo;{verbatim.quote}&rdquo;</VerbatimQuote>
      <VerbatimMeta>
        {verbatim.stars != null && (
          <>
            <StarRow>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} $filled={n <= verbatim.stars!}>
                  ★
                </Star>
              ))}
            </StarRow>
            <VerbatimMetaDot>·</VerbatimMetaDot>
          </>
        )}
        {verbatim.source && <VerbatimSource>{verbatim.source}</VerbatimSource>}
        {verbatim.date && (
          <>
            <VerbatimMetaDot>·</VerbatimMetaDot>
            <VerbatimSource>
              {new Date(verbatim.date).toLocaleDateString('en-US', {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'UTC',
              })}
            </VerbatimSource>
          </>
        )}
      </VerbatimMeta>
      {(verbatim.product || verbatim.brand) && (
        <VerbatimProductLine>
          {verbatim.brand && <span className="brand">{verbatim.brand}</span>}
          {verbatim.brand && verbatim.product && ' · '}
          {verbatim.product}
        </VerbatimProductLine>
      )}
    </VerbatimItem>
  )

  if (!verbatim.record_id || !projectId) return card

  return (
    <VerbatimPopup
      content={popoverContent}
      openOnHover
      nativeButton={false}
      initialFocus={false}
      side="bottom"
      align="start"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      {card}
    </VerbatimPopup>
  )
}

const PopoverLoadingWrap = styled.div`
  text-align: center;
  padding: 12px;
`

const PopoverBody = styled.div`
  padding: 8px 12px 0 0;
`

const PopoverHeader = styled.div`
  margin-bottom: 6px;
  margin-left: 12px;
  font-weight: 600;

  em {
    font-style: italic;
    font-weight: 600;
  }
`

const ViewRecordRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`

const ViewRecordLink = styled.a`
  color: #1890ff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;

  &:hover {
    color: #40a9ff;
    text-decoration: underline;
  }

  &:visited {
    color: #1890ff;
  }
`

const VerbatimPopup = styled(Popover)`
  max-width: 500px;
  max-height: 380px;
  overflow-y: auto;
`
